import { PropertyType, Locker, AVAILABLE_STATUS, NOT_AVAILABLE_SOLD_STATUS, NOT_AVAILABLE_OTHER_STATUS } from 'contexts/preferences/listing-params/types';
import { LOCALE_SEARCH_PATHS, LOCALE_MODAL_PATHS } from 'types/locales';
import { propertyTypeOptions, PropertyTypeOption, lockerSelectionOptions, notificationFrequencyOptions, inActiveNotificationFrequencyOptions, NotificationFrequency, bedroomBathroomParkingButtonGroupOptions } from './select-options';
import { SavedSearchListingStatus } from 'components/modal-dialog/saved-search-filters/search-criteria';
import type { SelectOption } from '@zoocasa/node-kit/components/controls/types';
import type { StatusOptions } from './select-options';

/**
 * Creates full internationalized property type options including all types
 * @param t - Translation function from useTranslation hook
 * @returns Array of all translated property type options
 */
export function getLocalizedPropertyTypeOptions(t: (key: string, options?: { defaultValue?: string }) => string): PropertyTypeOption[] {
  const localeKeyMap: Record<PropertyType, string> = {
    house: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_HOUSE,
    townhouse: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_TOWNHOUSE,
    condo: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_CONDO,
    land: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_LAND,
    commercial: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_COMMERCIAL,
    farm: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_FARM,
  };
  return propertyTypeOptions.map(option => ({
    value: option.value,
    label: t(localeKeyMap[option.value], { defaultValue: option.label }),
    ...(option.options && { options: option.options }),
  }));
}

/**
 * Creates internationalized locker options for the condo/townhouse filter
 * @param t - Translation function from useTranslation hook
 * @returns Array of translated locker options
 */
export function getLocalizedLockerOptions(t: (key: string, options?: { defaultValue?: string }) => string): SelectOption<Locker>[] {
  return lockerSelectionOptions
    .map(option => {
      let key: string | undefined;
      switch (option.value) {
        case 'any':
          key = LOCALE_MODAL_PATHS.FILTER_LOCKER_OPTIONS_ANY;
          break;
        case 'yes':
          key = LOCALE_MODAL_PATHS.FILTER_LOCKER_OPTIONS_YES;
          break;
        case 'no':
          key = LOCALE_MODAL_PATHS.FILTER_LOCKER_OPTIONS_NO;
          break;
        default:
          // Ignore unknown locker option values
          return null;
      }

      return {
        value: option.value,
        label: t(key, {
          defaultValue: option.label,
        }),
      };
    })
    .filter((option): option is SelectOption<Locker> => option !== null);
}

/**
 * Creates internationalized status options for listings
 * @param t - Translation function from useTranslation hook
 * @param isRental - Whether this is for rental listings
 * @param addsSuffix - Whether to add "Listings" suffix
 * @param statuses - Array of status options to include
 * @returns Array of translated status options
 */
export function getLocalizedStatusOptions(
  t: (key: string, options?: { defaultValue?: string; [key: string]: any }) => string,
  isRental = false,
  addsSuffix = false,
  statuses: readonly StatusOptions[] = [AVAILABLE_STATUS, NOT_AVAILABLE_SOLD_STATUS, NOT_AVAILABLE_OTHER_STATUS]
): SelectOption<StatusOptions>[] {
  const options: SelectOption<StatusOptions>[] = [];

  if (statuses.includes(AVAILABLE_STATUS)) {
    const defaultValue = addsSuffix ? 'Active Listings' : 'Active';
    options.push({
      value: AVAILABLE_STATUS,
      label: t(LOCALE_SEARCH_PATHS.FILTER_STATUS_ACTIVE, { defaultValue, suffix: addsSuffix ? ' Listings' : '' }),
    });
  }

  if (statuses.includes(NOT_AVAILABLE_SOLD_STATUS)) {    
    const defaultValue = addsSuffix
      ? (isRental ? 'Leased Listings' : 'Sold Listings')
      : (isRental ? 'Leased' : 'Sold');
    const statusKey = isRental ? LOCALE_SEARCH_PATHS.FILTER_STATUS_LEASED : LOCALE_SEARCH_PATHS.FILTER_STATUS_SOLD;
    options.push({
      value: NOT_AVAILABLE_SOLD_STATUS,
      label: t(statusKey, { defaultValue, suffix: addsSuffix ? ' Listings' : '' }),
    });
  }

  if (statuses.includes(NOT_AVAILABLE_OTHER_STATUS)) {
    const defaultValue = addsSuffix ? 'Expired Listings' : 'Expired';
    options.push({
      value: NOT_AVAILABLE_OTHER_STATUS,
      label: t(LOCALE_SEARCH_PATHS.FILTER_STATUS_EXPIRED, { defaultValue, suffix: addsSuffix ? ' Listings' : '' }),
    });
  }

  return options;
}

/**
 * Creates internationalized notification frequency options for saved searches
 * @param t - Translation function from useTranslation hook
 * @returns Array of translated notification frequency options
 */
export function getLocalizedNotificationFrequencyOptions(t: (key: string, options?: { defaultValue?: string }) => string): SelectOption<NotificationFrequency>[] {
  return notificationFrequencyOptions.map(option => {
    let key: string;
    switch (option.value) {
      case 0:
        key = LOCALE_SEARCH_PATHS.SAVED_SEARCH_NOTIFICATION_FREQUENCY_NO_ALERTS;
        break;
      case -1:
        key = LOCALE_SEARCH_PATHS.SAVED_SEARCH_NOTIFICATION_FREQUENCY_INSTANT;
        break;
      case 86400:
        key = LOCALE_SEARCH_PATHS.SAVED_SEARCH_NOTIFICATION_FREQUENCY_DAILY;
        break;
      case 604800:
        key = LOCALE_SEARCH_PATHS.SAVED_SEARCH_NOTIFICATION_FREQUENCY_WEEKLY;
        break;
      default:
        // Fallback for unknown values
        return { value: option.value, label: option.label };
    }

    return {
      value: option.value,
      label: t(key, { defaultValue: option.label }),
    };
  });
}

/**
 * Creates internationalized inactive notification frequency options for sold/inactive listings
 * @param t - Translation function from useTranslation hook
 * @returns Array of translated inactive notification frequency options
 */
export function getLocalizedInactiveNotificationFrequencyOptions(t: (key: string, options?: { defaultValue?: string }) => string): SelectOption<NotificationFrequency>[] {
  return inActiveNotificationFrequencyOptions.map(option => ({
    value: option.value,
    label: t(LOCALE_SEARCH_PATHS.SAVED_SEARCH_NOTIFICATION_FREQUENCY_NO_ALERTS_SOLD_INACTIVE, { defaultValue: option.label }),
  }));
}

/**
 * Creates internationalized bedroom, bathroom, and parking button group options
 * @param t - Translation function from useTranslation hook
 * @param isExactNumber - Whether to show exact numbers or ranges (1+ vs 1)
 * @returns Array of translated button group options
 */
export function getLocalizedBedroomBathroomParkingOptions(
  t: (key: string, options?: { defaultValue?: string }) => string,
  isExactNumber: boolean
): SelectOption<string>[] {
  const baseOptions = bedroomBathroomParkingButtonGroupOptions(isExactNumber);
  
  return baseOptions.map(option => {
    // Only translate "Any" option, numbers are universal
    if (option.value === '0+') {
      return {
        value: option.value,
        label: t(LOCALE_SEARCH_PATHS.FILTER_BUTTON_GROUPS_ANY, { defaultValue: option.label }),
      };
    }
    
    // Return numbers as-is since they don't need translation
    return { value: option.value, label: option.label };
  });
}

/**
 * Gets the localized label for listing date based on listing status
 * @param status - The listing status (Active, Sold, Leased, Expired, Pending)
 * @param t - Translation function from useTranslation hook
 * @returns Localized label for the listing date
 */
export function getLocalizedListingDateLabel(status: SavedSearchListingStatus, t: (key: string, options?: { defaultValue?: string; [key: string]: any }) => string): string {
  const statusKeyMap: Record<SavedSearchListingStatus, string> = {
    Active: LOCALE_SEARCH_PATHS.FILTER_STATUS_ACTIVE,
    Sold: LOCALE_SEARCH_PATHS.FILTER_STATUS_SOLD,
    Leased: LOCALE_SEARCH_PATHS.FILTER_STATUS_LEASED,
    Expired: LOCALE_SEARCH_PATHS.FILTER_STATUS_EXPIRED,
    Pending: LOCALE_SEARCH_PATHS.FILTER_STATUS_PENDING,
  };

  const translationKey = statusKeyMap[status];
  if (!translationKey) {
    return status; // Fallback to raw status if no key found
  }

  return t(translationKey, { defaultValue: status, suffix: '' });
}
